import React, { useEffect, useState, useCallback } from 'react';
import { Container, Row, Col, Form, Button, Alert, Table } from 'react-bootstrap';
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { purgeJobs } from '../../apis/jobApi';
import { getAdminConfiguration, putAdminConfiguration } from '../../apis/adminApi';
import { Spinner } from 'react-bootstrap';

export const FormattedConfig = ({ configEntryLabel, configValues, today, setAlert }) => {

    const [adminConfig, setAdminConfig] = useState(() => ({ [configEntryLabel]: configValues }));
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const formattingSettingsName = useCallback((settingsName) => {
        return settingsName
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/^./, str => str.toUpperCase());
    }, []);

    
    const validateConfig = useCallback((config) => {
        const fieldErrors = {};
        const settings = adminConfig[config];
        Object.entries(settings).forEach(([key, value]) => {
            if (typeof value === 'number') {
                if (!Number.isInteger(value) || value < 0) {
                    fieldErrors[key] = 'Must be a whole number (0 or greater).';
                }
            } else if (typeof value === 'string') {
                if (value.trim() === '') {
                    fieldErrors[key] = 'This field cannot be empty.';
                }
            }
        });
        return fieldErrors;
    }, [adminConfig]);

    const updateAdminConfiguration = useCallback(async (e, config) => {
        e.preventDefault();
        const fieldErrors = validateConfig(config);
        if (Object.keys(fieldErrors).length > 0) {
            setErrors(prev => ({ ...prev, [config]: fieldErrors }));
            return;
        }
        setErrors(prev => ({ ...prev, [config]: {} }));
        setSubmitting(true);
        try {
            await putAdminConfiguration(adminConfig);
            setAlert({ message: `${formattingSettingsName(config)} updated successfully!`, variant: "success" });
        } catch (error) {
            setAlert({ message: `Failed to update ${formattingSettingsName(config)}. Please try again.`, variant: "danger" });
        } finally {
            setSubmitting(false);
        }
    }, [adminConfig, formattingSettingsName, validateConfig]);
    
    const validJson = (obj) => {
        return obj !== null && typeof obj === 'object' && !Array.isArray(obj);
    }

    const handleInputChange = useCallback((e, settingKey, settingType) => {
        let updatedValue = e.target.value;

        if (settingType === 'number') { 
            updatedValue = Math.max(0, parseInt(updatedValue) || 0);
        } else if (settingType === 'date') {
            updatedValue = updatedValue < today ? today : updatedValue;
        } else if (settingType === 'boolean') {
            updatedValue = Boolean(e.target.checked);
        }
        
        setAdminConfig(prevConfig => ({
            ...prevConfig,
            [settingKey]: {
                ...prevConfig[settingKey],
                [e.target.name]: updatedValue
            }
        }));
        
        setErrors(prev =>{
            const mod = {...prev};
            delete mod[settingKey];
            return mod;
        });
    });

    return<> 
    <Form key={configEntryLabel} onSubmit={e => updateAdminConfiguration(e, configEntryLabel)}>
        <Row>
            <h4>{formattingSettingsName(configEntryLabel)}</h4>
        {
            
            !validJson(configValues) ? (
                <>                    
                    <Row>
                        <Col lg={6} md={12} sm={12} key={configEntryLabel}>
                            <Form.Group className="mb-3" controlId={`${configEntryLabel}-${configEntryLabel}`}>
                                <div className='admin-form'>
                                    <Col>
                                        <Form.Label> {formattingSettingsName(configEntryLabel)}</Form.Label>
                                    </Col>
                                    <Col>
                                        {typeof configValues === 'boolean' ? (
                                            
                                                <Form.Check
                                                    type="switch"
                                                    name={configEntryLabel}
                                                    checked={configValues}
                                                    onChange={e => handleInputChange(e, configEntryLabel, typeof configValues)}
                                                />
                                            ) : (
                                                <>
                                                <Form.Control
                                                    required
                                                    name={configEntryLabel}
                                                    className="admin-form-control"
                                                    type={typeof configValues}
                                                    isInvalid={!!errors[configEntryLabel]?.[configEntryLabel]}
                                                    min={typeof configValues === 'number' ? 0 : undefined}
                                                    onKeyDown={typeof configValues === 'number' ? (e) => {
                                                        if (e.key === 'e' || e.key === 'E' || e.key === '.' || e.key === ',') {
                                                            e.preventDefault();
                                                            setErrors(prev => ({
                                                                ...prev,
                                                                [configEntryLabel]: { ...prev[configEntryLabel], [configEntryLabel]: 'Only whole numbers are allowed.' }
                                                            }));
                                                        }
                                                    } : undefined}
                                                    onInput={e => handleInputChange(e, configEntryLabel, typeof configValues)}
                                                    defaultValue={configValues}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors[configEntryLabel]?.[configEntryLabel]}
                                                </Form.Control.Feedback>
                                                </>
                                            )}
                                    </Col>
                                </div>
                            </Form.Group>
                        </Col>
                    </Row>
                </>
            ) : (
                <>
                    <Row>
                        {Object.entries(configValues).map(([settingKey, setting]) => (
                                <Col lg={6} md={12} sm={12} key={settingKey}>
                                    <Form.Group className="mb-3" controlId={`${configEntryLabel}-${settingKey}`}>
                                        <div className="admin-form">
                                            <Col>
                                                <Form.Label>{formattingSettingsName(settingKey)}</Form.Label>
                                            </Col>
                                            <Col>
                                                {typeof setting === 'boolean' ? (
                                                    <Form.Check
                                                        type="switch"
                                                        name={settingKey}
                                                        checked={setting}
                                                        onChange={e => handleInputChange(e, configEntryLabel, typeof setting)}
                                                    />
                                                ) : (
                                                    <>
                                                    <Form.Control
                                                        required
                                                        name={settingKey}
                                                        className="admin-form-control"
                                                        type={typeof setting}
                                                        isInvalid={!!errors[configEntryLabel]?.[settingKey]}
                                                        min={typeof setting === 'number' ? 0 : undefined}
                                                        onKeyDown={typeof setting === 'number' ? (e) => {
                                                            if (e.key === 'e' || e.key === 'E' || e.key === '.' || e.key === ',') {
                                                                e.preventDefault();
                                                                setErrors(prev => ({
                                                                    ...prev,
                                                                    [configEntryLabel]: { ...prev[configEntryLabel], [settingKey]: 'Only whole numbers are allowed.' }
                                                                }));
                                                            }
                                                        } : undefined}
                                                        onInput={e => handleInputChange(e, configEntryLabel, typeof setting)}
                                                        defaultValue={setting}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors[configEntryLabel]?.[settingKey]}
                                                    </Form.Control.Feedback>
                                                    </>
                                                )}
                                            </Col>
                                        </div>
                                    </Form.Group>
                                </Col>
                        ))}
                    </Row>
                </>
            )
   
        }

        </Row>
                 
        <Row>
            <Col lg={{ offset: 11, span: 1 }} md={{ offset: 10, span: 2 }} sm={{ offset: 8, span: 2 }}>
                <Button className="button-table" type="submit" disabled={submitting}>
                    {submitting ? <Spinner size="sm" /> : 'Submit'}
                </Button>
            </Col>
        </Row>
    </Form>
    </>

}
